import React, { useState, useEffect } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import {
  Card,
  CardContent,
  Typography,
  Button,
  Box,
  Alert,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  FormControl,
  FormLabel,
  RadioGroup,
  FormControlLabel,
  Radio,
  Chip,
  Divider,
  CircularProgress,
  IconButton,
  Switch,
} from '@mui/material';
import {
  Refresh,
  PowerSettingsNew,
  ArrowBack,
  CheckCircle,
  Error,
  Warning,
  Visibility,
  VisibilityOff,
} from '@mui/icons-material';
import { ServerInfo } from '../types';
import { serverApi } from '../services/api';
import IpmiManagement from './IpmiManagement';

interface ServerManagementProps {}

const ServerManagement: React.FC<ServerManagementProps> = () => {
  const navigate = useNavigate();
  const { serviceName } = useParams<{ serviceName: string }>();
  const [serverInfo, setServerInfo] = useState<ServerInfo | null>(null);
  const [loading, setLoading] = useState(true);
  const [rebootLoading, setRebootLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [rebootDialogOpen, setRebootDialogOpen] = useState(false);
  const [rebootType, setRebootType] = useState<'soft' | 'hard'>('soft');
  const [monitoringLoading, setMonitoringLoading] = useState(false);

  // Функция для очистки значения процессора от префикса "ADVANCE-5 |"
  const formatProcessorName = (commercialRange: string): string => {
    return commercialRange.replace(/^ADVANCE-5\s*\|\s*/, '');
  };

  const fetchServerData = async () => {
    if (!serviceName) return;

    try {
      setLoading(true);
      setError(null);

      const serverResponse = await serverApi.getServerInfo(serviceName);

      if (serverResponse.success) {
        setServerInfo(serverResponse.data);
      }
    } catch (err) {
      setError('Не удалось загрузить информацию о сервере');
      console.error('Error fetching server data:', err);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchServerData();
  }, [serviceName]); // eslint-disable-line react-hooks/exhaustive-deps

  const handleReboot = async () => {
    if (!serviceName) return;

    try {
      setRebootLoading(true);
      const response = await serverApi.rebootServer(serviceName, rebootType);

      if (response.success) {
        setRebootDialogOpen(false);
        // Обновляем информацию о сервере через некоторое время
        setTimeout(() => {
          fetchServerData();
        }, 5000);
      } else {
        setError(response.message || 'Ошибка при перезагрузке сервера');
      }
    } catch (err) {
      setError('Не удалось перезагрузить сервер');
      console.error('Error rebooting server:', err);
    } finally {
      setRebootLoading(false);
    }
  };

  // Обработчик изменения мониторинга
  const handleMonitoringChange = async (enabled: boolean) => {
    if (!serverInfo || !serviceName) return;

    try {
      setMonitoringLoading(true);
      setError(null);

      // Когда мониторинг включен: monitoring=true, noIntervention=false (вмешательство разрешено)
      // Когда мониторинг выключен: monitoring=false, noIntervention=true (вмешательство запрещено)
      const response = await serverApi.updateMonitoring(
        serviceName,
        enabled,
        !enabled
      );

      if (response.success) {
        // Обновляем локальное состояние
        setServerInfo({
          ...serverInfo,
          monitoring: enabled,
          noIntervention: !enabled
        });
      } else {
        setError(response.message || 'Ошибка при обновлении мониторинга');
      }
    } catch (err) {
      setError('Не удалось обновить настройки мониторинга');
      console.error('Error updating monitoring:', err);
    } finally {
      setMonitoringLoading(false);
    }
  };

  const getStateIcon = (state?: string) => {
    switch (state) {
      case 'ok':
        return <CheckCircle color="success" />;
      case 'maintenance':
        return <Warning color="warning" />;
      case 'error':
        return <Error color="error" />;
      default:
        return <Warning color="disabled" />;
    }
  };

  const getStateText = (state?: string) => {
    switch (state) {
      case 'ok':
        return 'Работает';
      case 'maintenance':
        return 'Обслуживание';
      case 'error':
        return 'Ошибка';
      default:
        return 'Неизвестно';
    }
  };

  if (loading) {
    return (
      <Box display="flex" justifyContent="center" alignItems="center" minHeight="400px">
        <CircularProgress />
        <Typography variant="body1" sx={{ ml: 2 }}>
          Загрузка информации о сервере...
        </Typography>
      </Box>
    );
  }

  return (
    <Box>
      <Box display="flex" alignItems="center" mb={3}>
        <IconButton onClick={() => navigate('/')} sx={{ mr: 2 }}>
          <ArrowBack />
        </IconButton>
        <Typography variant="h4" component="h1">
          Управление сервером: {serviceName}
        </Typography>
      </Box>

      {error && (
        <Alert severity="error" sx={{ mb: 3 }} onClose={() => setError(null)}>
          {error}
        </Alert>
      )}

      <Box display="flex" flexDirection={{ xs: 'column', md: 'row' }} gap={3}>
        {/* Информация о сервере */}
        <Box flex={1}>
          <Card>
            <CardContent>
              <Typography variant="h6" gutterBottom>
                Информация о сервере
              </Typography>
              {serverInfo && (
                <Box>
                  <Box display="flex" alignItems="center" mb={2}>
                    {getStateIcon(serverInfo.state)}
                    <Typography variant="body1" sx={{ ml: 1 }}>
                      Статус: {getStateText(serverInfo.state)}
                    </Typography>
                  </Box>
                  
                  <Typography variant="body2" color="textSecondary" gutterBottom>
                    <strong>Название:</strong> {serverInfo.name}
                  </Typography>
                  <Typography variant="body2" color="textSecondary" gutterBottom>
                    <strong>IP адрес:</strong> {serverInfo.ip}
                  </Typography>
                  <Typography variant="body2" color="textSecondary" gutterBottom>
                    <strong>Обратный DNS:</strong> {serverInfo.reverse}
                  </Typography>
                  <Typography variant="body2" color="textSecondary" gutterBottom>
                    <strong>ОС:</strong> {serverInfo.os}
                  </Typography>
                  <Typography variant="body2" color="textSecondary" gutterBottom>
                    <strong>Датацентр:</strong> {serverInfo.datacenter}
                  </Typography>
                  <Typography variant="body2" color="textSecondary" gutterBottom>
                    <strong>Процессор:</strong> {formatProcessorName(serverInfo.commercialRange)}
                  </Typography>
                  
                  <Box mt={2}>
                    <Chip
                      label={serverInfo.rescue ? 'Rescue режим' : 'Обычный режим'}
                      color={serverInfo.rescue ? 'warning' : 'default'}
                      size="small"
                    />
                  </Box>

                  <Divider sx={{ my: 2 }} />

                  <Box>
                    <Typography variant="subtitle2" gutterBottom>
                      Настройки мониторинга
                    </Typography>

                    <Box display="flex" alignItems="center" justifyContent="space-between">
                      <Box display="flex" alignItems="center">
                        {serverInfo.monitoring ? <Visibility sx={{ mr: 1, color: 'success.main' }} /> : <VisibilityOff sx={{ mr: 1, color: 'text.secondary' }} />}
                        <Box>
                          <Typography variant="body2">
                            Мониторинг сервера
                          </Typography>
                          <Typography variant="caption" color="textSecondary">
                            ICMP мониторинг и автоматическое вмешательство
                          </Typography>
                        </Box>
                      </Box>
                      <Switch
                        checked={serverInfo.monitoring}
                        onChange={(e) => handleMonitoringChange(e.target.checked)}
                        disabled={monitoringLoading}
                        color="primary"
                      />
                    </Box>

                    <Typography variant="caption" color="textSecondary" sx={{ mt: 1, display: 'block' }}>
                      При включении: мониторинг доступности сервера через ICMP и автоматическое создание тикета в дата-центр при потере связи
                    </Typography>
                  </Box>
                </Box>
              )}
            </CardContent>
          </Card>
        </Box>

        {/* Управление сервером */}
        <Box flex={1}>
          <Card>
            <CardContent>
              <Typography variant="h6" gutterBottom>
                Управление
              </Typography>
              
              <Box display="flex" flexDirection="column" gap={2}>
                <Button
                  variant="contained"
                  color="warning"
                  startIcon={<PowerSettingsNew />}
                  onClick={() => setRebootDialogOpen(true)}
                  disabled={rebootLoading}
                >
                  {rebootLoading ? 'Перезагрузка...' : 'Перезагрузить сервер'}
                </Button>

                <Divider />

                <Box>
                  <Typography variant="subtitle2" gutterBottom>
                    IPMI управление
                  </Typography>
                  <IpmiManagement
                    serviceName={serviceName || ''}
                    serverMonitoring={serverInfo?.monitoring || false}
                  />
                </Box>

                <Button
                  variant="outlined"
                  startIcon={<Refresh />}
                  onClick={fetchServerData}
                  disabled={loading}
                >
                  Обновить информацию
                </Button>
              </Box>
            </CardContent>
          </Card>
        </Box>
      </Box>

      {/* Диалог перезагрузки */}
      <Dialog open={rebootDialogOpen} onClose={() => setRebootDialogOpen(false)}>
        <DialogTitle>Перезагрузка сервера</DialogTitle>
        <DialogContent>
          <Typography variant="body1" gutterBottom>
            Выберите тип перезагрузки для сервера {serviceName}:
          </Typography>
          <FormControl component="fieldset" sx={{ mt: 2 }}>
            <FormLabel component="legend">Тип перезагрузки</FormLabel>
            <RadioGroup
              value={rebootType}
              onChange={(e) => setRebootType(e.target.value as 'soft' | 'hard')}
            >
              <FormControlLabel
                value="soft"
                control={<Radio />}
                label="Мягкая перезагрузка (рекомендуется)"
              />
              <FormControlLabel
                value="hard"
                control={<Radio />}
                label="Жесткая перезагрузка"
              />
            </RadioGroup>
          </FormControl>
          <Alert severity="warning" sx={{ mt: 2 }}>
            Внимание: Перезагрузка сервера приведет к временной недоступности сервисов.
          </Alert>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setRebootDialogOpen(false)}>
            Отмена
          </Button>
          <Button
            onClick={handleReboot}
            color="warning"
            variant="contained"
            disabled={rebootLoading}
          >
            {rebootLoading ? 'Перезагрузка...' : 'Перезагрузить'}
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};

export default ServerManagement;
